import { useState, useEffect, useMemo, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import { createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
// Soft UI Dashboard PRO React example components
import Sidenav from "cxfcomponents/Sidenav";
// import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
// Soft UI Dashboard PRO React routes
// Soft UI Dashboard PRO React contexts
import { useSoftUIController } from "context";
import CookieConsent from "cxfcomponents/CookieConsent";
import SignInIllustration from "layouts/Signin/sign-in";
import RedirectToSignUp from "layouts/Signup/sign-up";
import TwoStepAuthentication from "layouts/Verification/twostepauthentication";
// Images
import cxfab_circle from "assets/images/cxfab_circle.png";
import brand from "assets/images/cxf_logo.png";
// import useAuth0
import AuthenticationGuard from "routes/AuthenticationGuard";
import ProtectedRoute from "authentication/ProtectedRoute";
import IdleTimeoutDialog from "layouts/Security/components/IdleTimeoutDialog";
import { setCookie } from "helpers/common";
import { UserContext } from "context/AppProvider/UserContext";
import { UsersStatusContext } from "context/AppProvider/UsersStatusContext";
import { useAuth0 } from "@auth0/auth0-react";
import Verification from "layouts/Verification/verification";
import { io } from "socket.io-client";
import NewFlowDialog from "cxfcomponents/NewFlowDialog";
import getDesignTokens from "assets/theme";
import {
	setThemeMode,
	setIsSystemTheme
} from "redux/actions/portal/settingsActionCreator";
import useThemeDetector from "hooks/useThemeDetector";
import "assets/theme/darkTheme.css";
import { THEME_TYPES } from "constants/theme";
import { setUdasSocket } from "redux/actions/portal/udasSocketActionCreator";
import { NewIntegrationDialog } from "cxfcomponents/Integrations/NewIntegrationDialog";
import { RegisterComponentDialog } from "./layouts/Flow Designer/components/RegisterComponentDialog";
import { usePermissions } from "hooks/usePermissions";
import { useUsers } from "hooks/useUsers";
import { useCurrentTenant } from "hooks/useCurrentTenant";
import useWatchOpenedTabs from "hooks/useWatchOpenedTabs";
import Maintenance from "layouts/Error/Maintenance";
import { UPSERT_PORTAL_USER_FLOWS, REMOVE_PORTAL_USER_FLOWS, UPDATE_USER_STATUS } from "redux/constants/portal";
import { UPSERT_DESIGNER_USER_FLOWS, REMOVE_DESIGNER_USER_FLOWS } from "redux/constants/flowDesigner";
import { setCurrentAuth0User } from "authentication/Auth0ProviderWithHistory";
import { defineCustomPrototypeFunctions } from "helpers/prototypes";
import { canParseJSON } from "helpers/common";
import { getEnv } from "helpers/environment";
import { getAppRoutes } from "routes";
import { useSetUserData } from "./hooks/useSetUserData";
import { AuthChecker } from "authentication/AuthChecker";
import { useRoles } from "hooks/useRoles";
import { isStaleSessionError, CROSS_TAB_LOGOUT_KEY } from "helpers/authErrors";

export const openFlows = new Map();

export let thisUsersData = null;

export let cacheUpdateSocketIoClient = null;

function App() {
	useUsers();
	useCurrentTenant();
	useRoles();
	const { userPermissions } = usePermissions();
	const [controller] = useSoftUIController();
	const { miniSidenav, direction, layout, sidenavColor } = controller;
	const [onMouseEnter, setOnMouseEnter] = useState(false);
	const [rtlCache, setRtlCache] = useState(null);
	const { pathname } = useLocation();
	const {
		user,
		isLoading,
		error,
		isAuthenticated,
		getAccessTokenSilently,
		logout
	} = useAuth0();
	const [isLoggedIn, setIsLoggedIn] = useState(false);
	const { userData } = useContext(UserContext);
	const { usersStatus, setUsersStatus } = useContext(UsersStatusContext);
	const { themeMode, isSystemTheme, isLogout } = useSelector(
		(store) => store?.portal?.settings
	);
	const { udasSocket } = useSelector((store) => store?.portal?.udasSocket);
	const [accessToken, setAccessToken] = useState(false);
	const rDispatch = useDispatch();
	const isDarkTheme = useThemeDetector();
	const currentUrl = window.location.protocol + "//" + window.location.host;
	const isOtpVerified = localStorage.getItem("isOtpVerified");

	useSetUserData(accessToken);
	useWatchOpenedTabs();

	useEffect(() => {
		if (!cacheUpdateSocketIoClient) {
			// Initalize socket.io client to request cache updates in the cxf-executor during certain portal operations (credential updates, connector replacements).
			const socketServerUrl = getEnv("EXECUTOR_API_SOCKET");
			
			cacheUpdateSocketIoClient = io(`${socketServerUrl}/updates`, {
				transports: ["websocket"],
				withCredentials: true
			}).on("connect", () => {
				console.info(`Successfully (re)connected to cxf-executor socket.io server "${socketServerUrl}/updates" for requesting cache updates`);
			}).on('disconnect', (reason) => {
				console.info(`Disconnected from cxf-executor socket.io server "${socketServerUrl}/updates" for requesting cache updates, reason: ${reason}`);
			});
		}
	}, []);

	const connectUserToUdasSocket = () => {
		rDispatch(
			setUdasSocket(
				io(getEnv("UDAS_API_SOCKET"), {
					transports: ["websocket"],
					withCredentials: true
				})
			)
		);
	};

	// Cache for the rtl
	useMemo(() => {
		const cacheRtl = createCache({
			key: "rtl",
			stylisPlugins: [rtlPlugin]
		});

		setRtlCache(cacheRtl);
	}, []);

	const handleOnMouseEnter = () => {
		if (miniSidenav && !onMouseEnter) {
			setOnMouseEnter(true);
		}
	};

	const handleOnMouseLeave = () => {
		if (onMouseEnter) {
			setOnMouseEnter(false);
		}
	};

	const loadTheme = () => {
		const user_preferences = canParseJSON(userData?.user_preferences)
			? JSON.parse(userData.user_preferences)
			: {};

		const isSystem =
			user_preferences?.user_preferences?.portal?.themeMode?.isSystem;
		const theme =
			user_preferences?.user_preferences?.portal?.themeMode?.theme;

		if (isSystem) {
			rDispatch(setIsSystemTheme(isSystem));
			if (isSystem) {
				rDispatch(
					setThemeMode(isDarkTheme ? THEME_TYPES.DARK : THEME_TYPES.LIGHT)
				);
				return;
			}
		}

		if (theme) {
			rDispatch(setThemeMode(theme));
		}
	};

	const updatePageTitle = () => {
		const keyRoute = getEnv("KEY_ROUTE") || "dev";
		const currentUrl = window.location.href;
		const titles = {
			dev: {
				portal: "DEV - CXFabric",
				designer: "DEV - CXFabric Flow Designer"
			},
			qa: {
				portal: "QA - CXFabric",
				designer: "QA - CXFabric Flow Designer"
			},
			prod: {
				portal: "CXFabric",
				designer: "CXFabric Flow Designer"
			}
		};

		document.title =
			titles[keyRoute][
				currentUrl.includes("/designer") ? "designer" : "portal"
			];
	};

	const getRoutes = (allRoutes) => {
		const userPreference = canParseJSON(userData?.user_preferences)
			? JSON.parse(userData.user_preferences)
			: {};
		const twoStepEnabled = userPreference?.twostepauthentication ?? false;

		return allRoutes
			.map((route) => {
				if (route.collapse) {
					return getRoutes(route.collapse);
				}
				if (route.route) {
					return (
						<Route
							path={route.route}
							element={
								!isAuthenticated ? (
									<Navigate to="/authentication/sign-in" />
								) : !user?.email_verified && isAuthenticated ? (
									<Navigate to="/authentication/verification" />
								) : !isOtpVerified &&
								  twoStepEnabled &&
								  isAuthenticated &&
								  !isLogout ? (
									<Navigate to="/authentication/otp" />
								) : (
									<ProtectedRoute
										route={route.route}
										userPermissions={userPermissions}
									>
										<AuthenticationGuard
											component={route.component}
										/>
									</ProtectedRoute>
								)
							}
							key={route.key}
						/>
					);
				}
				return null;
			})
			.flat();
	};

	const renderSidenav = () => {
		const userPreference = canParseJSON(userData?.user_preferences)
			? JSON.parse(userData.user_preferences)
			: {};
		const twoStepEnabled = userData
			? (userPreference?.twostepauthentication ?? false)
			: true;

		if (twoStepEnabled && !isOtpVerified && !isLogout) {
			return null;
		}

		if (
			layout === "dashboard" &&
			isAuthenticated &&
			user?.email_verified &&
			!pathname.includes("designer")
		) {
			return direction === "rtl" ? (
				<Sidenav
					color={sidenavColor}
					brand={brand}
					brandName="CXFabric"
					routes={getAppRoutes()}
					cxfab_circle={cxfab_circle}
				/>
			) : (
				<Sidenav
					color={sidenavColor}
					brand={brand}
					brandName="CXFabric"
					routes={getAppRoutes()}
					onMouseEnter={handleOnMouseEnter}
					onMouseLeave={handleOnMouseLeave}
				/>
			);
		}
	};

	const theme = useMemo(
		() => createTheme(getDesignTokens(themeMode)),
		[themeMode]
	);

	const renderApp = (appTheme) => {
		if (
			getEnv("PORTAL_UNDER_MAINTENANCE") &&
			!localStorage.getItem("allowToUsePortalUnderMaintenance")
		) {
			return (
				<ThemeProvider theme={appTheme}>
					<CssBaseline enableColorScheme />
					<Maintenance />
				</ThemeProvider>
			);
		}

		return (
			<ThemeProvider theme={appTheme}>
				<CssBaseline enableColorScheme />
				{isLoggedIn && <IdleTimeoutDialog />}
				{isLoggedIn && <CookieConsent />}
				{isLoggedIn && <NewFlowDialog />}
				{isLoggedIn && <NewIntegrationDialog />}
				{isLoggedIn && <RegisterComponentDialog dialogType="Connector" />}
				{isLoggedIn && renderSidenav()}
				<Routes>
					<Route
						exact
						path={"/authentication/sign-in"}
						element={<SignInIllustration />}
					/>
					<Route
						path="/authentication/verification"
						element={<Verification />}
					/>
					<Route
						exact
						path="/authentication/sign-up"
						element={<RedirectToSignUp />}
					/>
					<Route
						path={"/authentication/otp"}
						element={
							!isAuthenticated ? (
								<Navigate to="/authentication/sign-in" />
							) : !user?.email_verified && isAuthenticated ? (
								<Navigate to="/authentication/verification" />
							) : (
								<ProtectedRoute
									route={"/authentication/otp"}
									userPermissions={userPermissions}
								>
									<AuthenticationGuard
										component={<TwoStepAuthentication />}
									/>
								</ProtectedRoute>
							)
						}
					/>
					{getRoutes(getAppRoutes())}
					<Route
						path="*"
						element={<AuthChecker />}
					/>
				</Routes>
			</ThemeProvider>
		);
	};

	useEffect(() => {
		if (!udasSocket) return;

		console.log("Connecting to udas socket...");
		// Establish socket connection
		udasSocket.on("connect", () => {
			udasSocket.emit("joinRoom", user?.stripeId);
			udasSocket.emit("update_user_status", {
				tenant_id: user?.stripeId,
				auth0_id: user?.cxfUserId,
				status: "active"
			});
			console.log("Connected to udas socket");
		});

		// Listen when an invited user status changed to active
		udasSocket.on("userStatusUpdate", (payload) => {
			const userAuth0Id = payload.auth0_id.includes("auth0")
				? payload.auth0_id
				: `auth0|${payload.auth0_id}`;
			rDispatch({
				type: UPDATE_USER_STATUS,
				payload: {
					...payload,
					auth0_id: userAuth0Id
				}
			});
		});

		// Listen for user status updates
		udasSocket.on("users_status", (users_status) => {
			if (!Array.isArray(users_status)) {
				console.log("Invalid users_status data");
				setUsersStatus([]);
			} else {
				setUsersStatus(users_status);
			}
		});

		const handleUserFlowsUpserted = (userFlows) => {
			rDispatch({ type: UPSERT_PORTAL_USER_FLOWS, payload: userFlows });
			rDispatch({ type: UPSERT_DESIGNER_USER_FLOWS, payload: userFlows });
		};

		udasSocket.on("userFlowsCreated", handleUserFlowsUpserted);
		udasSocket.on("userFlowsUpdated", handleUserFlowsUpserted);

		const handleUserFlowsDeleted = (userFlowIds) => {
			rDispatch({ type: REMOVE_PORTAL_USER_FLOWS, payload: userFlowIds });
			rDispatch({ type: REMOVE_DESIGNER_USER_FLOWS, payload: userFlowIds });
		};

		udasSocket.on("userFlowsDeleted", handleUserFlowsDeleted);

		// Handle socket disconnection
		udasSocket.on("disconnect", (message) => {
			console.log("Disconnected from udas socket:", message);
		});

		return () => {
			udasSocket.off("userFlowsCreated", handleUserFlowsUpserted);
			udasSocket.off("userFlowsUpdated", handleUserFlowsUpserted);
			udasSocket.off("userFlowsDeleted", handleUserFlowsDeleted);
		};
	}, [udasSocket]);

	// Setting the dir attribute for the body element
	useEffect(() => {
		document.body.setAttribute("dir", direction);
	}, [direction]);

	useEffect(() => {
		document.body.classList[
			themeMode === THEME_TYPES.DARK ? "add" : "remove"
		]("dark-theme");
		if (user?.stripeId) {
			localStorage.setItem(`themeMode_${user?.stripeId}`, themeMode);
			localStorage.setItem(`isSystemTheme_${user?.stripeId}`, isSystemTheme);
		}
	}, [themeMode, isSystemTheme]);

	// Setting page scroll to 0 when changing the route
	useEffect(() => {
		document.documentElement.scrollTop = 0;
		document.scrollingElement.scrollTop = 0;
	}, [pathname]);

	useEffect(() => {
		updatePageTitle();
		defineCustomPrototypeFunctions();

		if (!getEnv("PORTAL_UNDER_MAINTENANCE")) {
			localStorage.removeItem("allowToUsePortalUnderMaintenance");

			return;
		}

		if (location.pathname === "/bypass") {
			localStorage.setItem("allowToUsePortalUnderMaintenance", true);
		} else if (location.pathname === "/clearbypass") {
			localStorage.removeItem("allowToUsePortalUnderMaintenance");
		}
	}, []);

	useEffect(() => {
		const fetchToken = async () => {
			try {
				const token = await getAccessTokenSilently();

				if (token) {
					setAccessToken(true);
					setCookie("accessToken", token); // set accessToken cookie
				}
			} catch (error) {
				console.error("Error fetching access token:", error);
				if (isStaleSessionError(error)) {
					logout({ logoutParams: { returnTo: currentUrl + "/authentication/sign-in" } });
				}
			}
		};

		if (isAuthenticated) {
			setCurrentAuth0User(user);
			setIsLoggedIn(isAuthenticated);
			fetchToken();
			connectUserToUdasSocket();

			return;
		}

		setCookie("accessToken"); // delete accessToken cookie
		setIsLoggedIn(false);
	}, [isAuthenticated]);

	useEffect(() => {
		if (isAuthenticated && userData) {
			loadTheme();
			thisUsersData = userData;
		}
	}, [isAuthenticated, userData]);

	useEffect(() => {
		//Log out from all browser tabs (Portal and Editor)
		if (isLogout && isAuthenticated) {
			// rDispatch(setIsLogout(false));
			logout({
				logoutParams: { returnTo: currentUrl + "/authentication/sign-in" }
			});
		}
	}, [isLogout]);

	useEffect(() => {
		// When another tab logs out, mirror the logout in this tab
		const handleCrossTabLogout = (event) => {
			if (event.key === CROSS_TAB_LOGOUT_KEY && event.newValue && isAuthenticated) {
				logout({ logoutParams: { returnTo: currentUrl + "/authentication/sign-in" } });
			}
		};
		window.addEventListener("storage", handleCrossTabLogout);
		return () => window.removeEventListener("storage", handleCrossTabLogout);
	}, [isAuthenticated]);

	useEffect(() => {
		// Reconnect to udas when change tab
		const handleVisibilityChange = () => {
			if (
				document.visibilityState === "visible" &&
				isAuthenticated &&
				usersStatus?.find((u) => u.auth0_id === user?.cxfUserId)?.status ===
					"inactive"
			) {
				connectUserToUdasSocket();
			}
		};

		// Add the event listener when the component mounts
		document.addEventListener("visibilitychange", handleVisibilityChange);

		// Remove the event listener when the component unmounts
		return () => {
			document.removeEventListener(
				"visibilitychange",
				handleVisibilityChange
			);
		};
	}, [isAuthenticated, user, usersStatus]);

	const origError = console.error;
	console.error = (...args) => {
		if (args[0]?.includes?.("Accessing element.ref")) {
			console.trace("REF ISSUE HERE");
		}
		origError.apply(console, args);
	};

	if (error) {
		return <div>Oops... {error.message}</div>;
	}

	if (isLoading && !isAuthenticated) {
		return <></>;
	}

	return direction === "rtl" ? (
		<CacheProvider value={rtlCache}>{renderApp(themeRTL)}</CacheProvider>
	) : (
		renderApp(theme)
	);
}

export default App;
